// Smart date suggestions and IP-based zip code detection for Eventism

/**
 * Get the next N busiest event industry dates within the next 90 days.
 * Returns [{label, date (YYYY-MM-DD), type}]
 */
export function getUpcomingEventDates(count = 4) {
  const today = new Date();
  const cutoff = new Date(today);
  cutoff.setDate(cutoff.getDate() + 90);
  const year = today.getFullYear();
  const nextYear = year + 1;

  // All major event industry peak dates (US-centric)
  const allDates = [
    // Q1
    { label: "Valentine's Day", month: 1, day: 14, type: "holiday" },
    { label: "Presidents' Day Weekend", month: 1, day: 17, type: "corporate" },
    { label: "St. Patrick's Day", month: 2, day: 17, type: "holiday" },
    // Q2 - Wedding & Graduation Season
    { label: "Easter Weekend", month: null, day: null, type: "holiday", compute: "easter" },
    { label: "Cinco de Mayo", month: 4, day: 5, type: "holiday" },
    { label: "Mother's Day", month: null, day: null, type: "holiday", compute: "mothers_day" },
    { label: "Memorial Day Weekend", month: null, day: null, type: "holiday", compute: "memorial_day" },
    { label: "Graduation Season", month: 4, day: 20, type: "peak_season" },
    // Q3 - Peak Wedding Season
    { label: "Father's Day", month: null, day: null, type: "holiday", compute: "fathers_day" },
    { label: "4th of July", month: 6, day: 4, type: "holiday" },
    { label: "Peak Wedding Season", month: 5, day: 15, type: "peak_season" },
    { label: "Labor Day Weekend", month: null, day: null, type: "holiday", compute: "labor_day" },
    // Q4 - Corporate & Holiday Season
    { label: "Halloween", month: 9, day: 31, type: "holiday" },
    { label: "Thanksgiving", month: null, day: null, type: "holiday", compute: "thanksgiving" },
    { label: "Holiday Party Season", month: 11, day: 12, type: "peak_season" },
    { label: "New Year's Eve", month: 11, day: 31, type: "holiday" },
    // Annual corporate peaks
    { label: "Q1 Kickoff Season", month: 0, day: 15, type: "corporate" },
    { label: "Mid-Year Corporate Events", month: 5, day: 20, type: "corporate" },
    { label: "Q4 Corporate Events", month: 9, day: 15, type: "corporate" },
  ];

  function getEaster(y) {
    const a = y % 19, b = Math.floor(y / 100), c = y % 100;
    const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(y, month, day);
  }

  function nthDayOfMonth(y, month, dayOfWeek, n) {
    const first = new Date(y, month, 1);
    let d = first.getDay();
    let offset = (dayOfWeek - d + 7) % 7;
    return new Date(y, month, 1 + offset + (n - 1) * 7);
  }

  function lastMondayOfMay(y) {
    const d = new Date(y, 4, 31);
    while (d.getDay() !== 1) d.setDate(d.getDate() - 1);
    return d;
  }

  const results = [];
  for (const evt of allDates) {
    for (const y of [year, nextYear]) {
      let date;
      if (evt.compute === "easter") {
        date = getEaster(y);
      } else if (evt.compute === "mothers_day") {
        date = nthDayOfMonth(y, 4, 0, 2); // 2nd Sunday of May
      } else if (evt.compute === "fathers_day") {
        date = nthDayOfMonth(y, 5, 0, 3); // 3rd Sunday of June
      } else if (evt.compute === "memorial_day") {
        date = lastMondayOfMay(y);
      } else if (evt.compute === "labor_day") {
        date = nthDayOfMonth(y, 8, 1, 1); // 1st Monday of September
      } else if (evt.compute === "thanksgiving") {
        date = nthDayOfMonth(y, 10, 4, 4); // 4th Thursday of November
      } else {
        date = new Date(y, evt.month, evt.day);
      }

      if (date > today && date <= cutoff) {
        const iso = date.toISOString().split('T')[0];
        if (!results.find(r => r.date === iso)) {
          results.push({ label: evt.label, date: iso, type: evt.type });
        }
      }
    }
  }

  // Sort by date, return the requested count
  results.sort((a, b) => new Date(a.date) - new Date(b.date));
  return results.slice(0, count);
}

/**
 * Get the default date (the latest of the suggested dates).
 */
export function getDefaultEventDate() {
  const dates = getUpcomingEventDates(4);
  return dates.length > 0 ? dates[dates.length - 1].date : '';
}

/**
 * Fetch approximate zip code from user's IP address.
 * Uses ipapi.co free tier (no key needed, ~1000 requests/day).
 */
export async function detectZipFromIP() {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    const res = await fetch('https://ipapi.co/json/', { signal: controller.signal }).catch(() => null);
    clearTimeout(timeout);
    if (!res || !res.ok) return null;
    const data = await res.json();
    return data.postal || null;
  } catch {
    return null;
  }
}

/**
 * Format a date string for display: "Sat, May 10"
 */
export function formatDateShort(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
